import { UrlKeys } from '@paragon/environment-tools';

import { getServerUrls } from './dataConfiguration';
import {
  blobFileMiddleware,
  combineMiddleware,
  emailFileMiddleware,
  errorHandlingMiddleware,
  Middleware,
  requireDownloadableFileMiddleware,
  simpleRequestMiddleware,
  urlFileMiddleware,
} from './middleware';
import { paragonFetch, ParagonFetchResult } from './paragonFetch';
import { DownloadableFileResponse, EmailResponse, FileResponse, NoFileContentResponse } from './responses';

/**
 * This is what ASP.NET servers respond with when returning with a status code like NotFound.
 */
export interface AspNetStatusCodeResult {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly traceId: string;
}

interface RequestOptions {
  data?: string | object | null;
  params?: URLSearchParams;
  urlEncode?: boolean;

  cancellationPromise?: PromiseLike<void>;
  responseType?: 'json-or-text' | 'blob' | 'stream';

  /**
   * If provided, this middleware will be used instead of the default middleware (error handling + json parsing + any
   *   middleware configured on the connection). Global middleware will still be applied and can not be overridden.
   */
  overrideMiddleware?: Middleware<ParagonFetchResult, FileResponse>;
}

interface FileRequestOptions extends RequestOptions {
  post?: boolean;
  mightEmail?: boolean;
  asUrl?: boolean;
}

const defaultFileOptions = {
  post: false,
  mightEmail: false,
  asUrl: false,
};

export class ServerConnection {
  serverName: UrlKeys;
  connectionMiddleware?: Middleware<unknown, unknown>;

  static globalMiddleware?: Middleware<ParagonFetchResult, ParagonFetchResult>;

  constructor(serverName: UrlKeys, middleware?: Middleware<unknown, unknown>) {
    this.serverName = serverName;
    this.connectionMiddleware = middleware;
  }

  get baseUrl() {
    return getServerUrls()![this.serverName];
  }

  applyMiddleware(middleware: Middleware<unknown, unknown>) {
    if (this.connectionMiddleware) {
      this.connectionMiddleware = combineMiddleware(this.connectionMiddleware, middleware);
    } else {
      this.connectionMiddleware = middleware;
    }
  }

  static applyGlobalMiddleware(middleware: Middleware<ParagonFetchResult, ParagonFetchResult>) {
    if (ServerConnection.globalMiddleware) {
      ServerConnection.globalMiddleware = combineMiddleware(ServerConnection.globalMiddleware, middleware);
    } else {
      ServerConnection.globalMiddleware = middleware;
    }
  }

  put<ResponseType = unknown>(url: string, options?: RequestOptions): Promise<ResponseType> {
    return this.runRequest('put', url, options) as Promise<ResponseType>;
  }

  get<ResponseType = unknown>(url: string, options?: RequestOptions): Promise<ResponseType> {
    return this.runRequest('get', url, options) as Promise<ResponseType>;
  }

  post<ResponseType = unknown>(url: string, options?: RequestOptions): Promise<ResponseType> {
    return this.runRequest('post', url, options) as Promise<ResponseType>;
  }

  patch<ResponseType = unknown>(url: string, options?: RequestOptions): Promise<ResponseType> {
    return this.runRequest('patch', url, options) as Promise<ResponseType>;
  }

  delete<ResponseType = unknown>(url: string, options?: RequestOptions): Promise<ResponseType> {
    return this.runRequest('delete', url, options) as Promise<ResponseType>;
  }

  fetchFile(
    url: string,
    options: FileRequestOptions & { mightEmail: true },
  ): Promise<DownloadableFileResponse | EmailResponse>;
  fetchFile(
    url: string,
    options: FileRequestOptions & { potentiallyNoFileContent: true },
  ): Promise<DownloadableFileResponse | NoFileContentResponse>;
  fetchFile(url: string, options?: FileRequestOptions): Promise<DownloadableFileResponse>;
  fetchFile(url: string, options: FileRequestOptions = defaultFileOptions) {
    const method = options.post ? 'post' : 'get';
    const responseType = options.asUrl ? 'json-or-text' : 'blob';

    const fileMiddleware = options.asUrl ? urlFileMiddleware : blobFileMiddleware;
    const middlewareWithErrorHandling = combineMiddleware(errorHandlingMiddleware, fileMiddleware);
    const combinedMiddleware = options.mightEmail
      ? combineMiddleware(middlewareWithErrorHandling, emailFileMiddleware)
      : combineMiddleware(middlewareWithErrorHandling, requireDownloadableFileMiddleware);

    return this.runRequest(method, url, {
      responseType,
      overrideMiddleware: combinedMiddleware,

      ...options,
    });
  }

  private runRequest(method: string, url: string, options?: RequestOptions): Promise<unknown> {
    const requestUrl = this.baseUrl + url;

    const requestPromise = paragonFetch(method.toUpperCase(), requestUrl, {
      includeAuthorizationHeader: true,
      encodeRoute: options?.urlEncode ?? true,
      responseType: options?.responseType,
      cancellationPromise: options?.cancellationPromise,

      data: options?.data,
      params: options?.params,
    });

    let middleware;

    if (options?.overrideMiddleware) {
      middleware = options.overrideMiddleware;
    } else {
      middleware = simpleRequestMiddleware;

      if (this.connectionMiddleware) {
        middleware = combineMiddleware(middleware, this.connectionMiddleware);
      }
    }

    if (ServerConnection.globalMiddleware) {
      middleware = combineMiddleware(ServerConnection.globalMiddleware, middleware);
    }

    return middleware(requestPromise);
  }
}
